import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Image, Upload, X, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ImageSelector({ 
  value, 
  onChange, 
  label = "Imagem (opcional)",
  showUpload = true,
  category = "campaign"
}) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageCache, setImageCache] = useState({});

  // Carregar imagem como blob (com autenticação)
  const loadImageBlob = async (imageId) => {
    if (imageCache[imageId]) return imageCache[imageId];
    
    try {
      const token = localStorage.getItem('nexus-token');
      const response = await fetch(`${API_URL}/api/images/${imageId}/file`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setImageCache(prev => ({ ...prev, [imageId]: blobUrl }));
        return blobUrl;
      }
    } catch (error) {
      console.error('Erro ao carregar imagem:', error);
    }
    return null;
  };

  // Buscar imagens ao abrir o modal
  const fetchImages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const url = category ? `${API_URL}/api/images?category=${category}` : `${API_URL}/api/images`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setImages(data);
        // Carregar previews de todas as imagens
        data.forEach(img => loadImageBlob(img.id));
      }
    } catch (error) {
      console.error('Erro ao buscar imagens:', error);
    } finally {
      setLoading(false);
    }
  };

  // Carregar preview quando value muda
  useEffect(() => {
    const loadPreview = async () => {
      if (value) {
        // Se já é uma URL blob, usar direto
        if (value.startsWith('blob:')) {
          setPreviewUrl(value);
        } else if (value.startsWith('http')) {
          setPreviewUrl(value);
        } else {
          // É um ID, carregar como blob
          const blobUrl = await loadImageBlob(value);
          if (blobUrl) {
            setPreviewUrl(blobUrl);
          }
        }
      } else {
        setPreviewUrl(null);
      }
    };
    loadPreview();
  }, [value]);

  const handleOpenModal = () => {
    setModalOpen(true);
    fetchImages();
  };

  const handleSelectImage = (image) => {
    setSelectedImage(image);
  };

  const handleConfirmSelection = () => {
    if (selectedImage) {
      onChange(selectedImage.url, selectedImage.id);
      setPreviewUrl(`${API_URL}${selectedImage.url}`);
    }
    setModalOpen(false);
    setSelectedImage(null);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem válida');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);

      const res = await fetch(`${API_URL}/api/images`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Imagem enviada!');
        onChange(data.url, data.id);
        setPreviewUrl(`${API_URL}${data.url}`);
        // Atualizar lista de imagens
        fetchImages();
      } else {
        toast.error('Erro ao enviar imagem');
      }
    } catch (error) {
      toast.error('Erro ao enviar imagem');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemove = () => {
    onChange(null, null);
    setPreviewUrl(null);
  };

  return (
    <div className="space-y-2">
      <Label className="text-foreground flex items-center gap-2 text-sm">
        <Image className="w-4 h-4" />
        {label}
      </Label>
      
      <div className="flex gap-2">
        {/* Botão para abrir galeria */}
        <Button 
          type="button" 
          variant="outline" 
          onClick={handleOpenModal}
          className="flex-1 justify-start border-border"
        >
          <Image className="w-4 h-4 mr-2" />
          {value ? 'Trocar imagem' : 'Escolher da galeria'}
        </Button>

        {/* Botão de upload */}
        {showUpload && (
          <div className="relative">
            <input
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={uploading}
            />
            <Button type="button" variant="outline" disabled={uploading} className="border-border">
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Preview da imagem selecionada */}
      {previewUrl && (
        <div className="relative inline-block mt-2">
          <img 
            src={previewUrl}
            alt="Preview"
            className="h-24 rounded-lg object-cover border border-border"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center hover:bg-destructive/80"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Modal de seleção de imagem */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Escolher Imagem</DialogTitle>
          </DialogHeader>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhuma imagem encontrada</p>
              <p className="text-sm mt-2">Faça upload de uma imagem usando o botão acima</p>
            </div>
          ) : (
            <>
              <ScrollArea className="h-[400px] pr-4">
                <div className="grid grid-cols-3 gap-3">
                  {images.map((img) => (
                    <div
                      key={img.id}
                      onClick={() => handleSelectImage(img)}
                      className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage?.id === img.id 
                          ? 'border-primary ring-2 ring-primary/30' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      {imageCache[img.id] ? (
                        <img
                          src={imageCache[img.id]}
                          alt={img.original_name || img.filename}
                          className="w-full h-28 object-cover"
                        />
                      ) : (
                        <div className="w-full h-28 bg-secondary flex items-center justify-center">
                          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1">
                        <p className="text-xs text-white truncate">
                          {img.original_name || img.filename}
                        </p>
                      </div>
                      {selectedImage?.id === img.id && (
                        <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-primary-foreground" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setModalOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleConfirmSelection} 
                  disabled={!selectedImage}
                >
                  Selecionar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ImageSelector;
